import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, dateFmt, STAGE_LABEL, errMsg, HOTEL_BRANDS, LEAD_CATEGORIES, DEFAULT_HOTEL_IMAGE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import GmRateRequests from "@/components/GmRateRequests";
import SpaceTracker from "@/components/SpaceTracker";
import EventsCalendar from "@/components/EventsCalendar";
import FunctionDiary from "@/components/FunctionDiary";
import AccountsPanel from "@/components/AccountsPanel";
import BookingsBoard from "@/components/BookingsBoard";
import SalesReport from "@/components/SalesReport";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  FileText, Handshake, CalendarCheck, CurrencyDollar, Buildings, ChatsCircle,
  PlusCircle, Trash, Star, MapPin, Target, Users, UserPlus, Trophy, UploadSimple,
  MagnifyingGlass, BellRinging, Tag, Warning, LinkSimple,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={26} weight="duotone" color={accent || "#002FA7"} />
      <div className="font-display font-black text-3xl text-[#111827] mt-3">{value}</div>
      <div className="text-sm text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}

function HotelDialog({ hotel, open, onOpenChange, trigger }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isEdit = !!hotel;
  const defaultBrand = user?.brand && HOTEL_BRANDS.includes(user.brand) ? user.brand : "Independent";
  const init = () => ({
    name: hotel?.name || "",
    brand: hotel ? (HOTEL_BRANDS.includes(hotel.brand) ? hotel.brand : "Other (custom)") : defaultBrand,
    city: hotel?.city || "",
    state: hotel?.state || "OK",
    zip_code: hotel?.zip_code || "",
    base_rate: hotel?.base_rate ?? 89,
    address: hotel?.address || "",
  });
  const [f, setF] = useState(init);
  const [customBrand, setCustomBrand] = useState(
    hotel && !HOTEL_BRANDS.includes(hotel.brand) ? hotel.brand
      : (user?.brand && !HOTEL_BRANDS.includes(user.brand) ? user.brand : ""));
  const [imageUrl, setImageUrl] = useState("");
  const [images, setImages] = useState(hotel?.images || []);

  useEffect(() => {
    if (open) { setF(init()); setImages(hotel?.images || []);
      setCustomBrand(hotel && !HOTEL_BRANDS.includes(hotel.brand) ? hotel.brand : ""); }
    // eslint-disable-next-line
  }, [open]);

  const mut = useMutation({
    mutationFn: (body) => isEdit ? api.updateHotel(hotel.id, body) : api.createHotel(body),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(isEdit ? "Listing updated & re-geocoded" : "Listing published & geocoded");
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e, "Could not save listing")),
  });
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const addUrl = () => {
    const u = imageUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) return toast.error("Enter a full image URL (https://…)");
    setImages((arr) => [...arr, u]); setImageUrl("");
  };
  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 3 * 1024 * 1024) return toast.error(`${file.name} is over 3MB`);
      const reader = new FileReader();
      reader.onload = () => setImages((arr) => [...arr, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeImg = (i) => setImages((arr) => arr.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="rounded-none border-[#111827] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit Listing" : "New Hotel Listing"}</DialogTitle></DialogHeader>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Hotel name</span>
          <input data-testid="hotel-name" value={f.name} onChange={F("name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Street address</span>
          <input data-testid="hotel-address" value={f.address} onChange={F("address")} placeholder="123 Main St" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">City</span>
            <input data-testid="hotel-city" value={f.city} onChange={F("city")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">State</span>
            <input data-testid="hotel-state" value={f.state} onChange={F("state")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Zip code (improves location accuracy)</span>
            <input data-testid="hotel-zip" value={f.zip_code} onChange={F("zip_code")} placeholder="81501" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Base rate/nt</span>
            <input data-testid="hotel-rate" type="number" value={f.base_rate} onChange={F("base_rate")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        </div>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Brand</span>
          <select data-testid="hotel-brand" value={f.brand} onChange={F("brand")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {HOTEL_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select></label>
        {f.brand === "Other (custom)" && (
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Custom brand name</span>
            <input data-testid="hotel-brand-custom" value={customBrand} onChange={(e) => setCustomBrand(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        )}

        {/* Photos */}
        <div className="text-sm">
          <span className="overline text-[#4B5563] block mb-1">Photos — paste an image URL or upload</span>
          <div className="flex gap-2">
            <input data-testid="hotel-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://your-website.com/photo.jpg"
              className="flex-1 border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <button type="button" data-testid="hotel-image-add" onClick={addUrl}
              className="bg-[#111827] text-white px-4 text-sm font-semibold hover:bg-[#002FA7] transition-colors">Add</button>
          </div>
          <label data-testid="hotel-image-upload-label" className="mt-2 flex items-center justify-center gap-2 border border-dashed border-[#9CA3AF] py-3 text-sm text-[#4B5563] cursor-pointer hover:border-[#002FA7] transition-colors">
            <UploadSimple size={18} weight="bold" /> Upload from device
            <input data-testid="hotel-image-file" type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
          </label>
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="hotel-image-previews">
              {images.map((src, i) => (
                <div key={i} className="relative w-20 h-16">
                  <img src={src} alt="" className="w-full h-full object-cover border border-[#E5E7EB]" />
                  <button type="button" data-testid={`hotel-image-remove-${i}`} onClick={() => removeImg(i)}
                    className="absolute -top-2 -right-2 bg-[#DC2626] text-white w-5 h-5 flex items-center justify-center text-xs">×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button data-testid="save-hotel-btn" disabled={mut.isPending}
            onClick={() => { if (!f.name.trim() || !f.city.trim()) return toast.error("Name and city required");
              const brand = f.brand === "Other (custom)" ? (customBrand || "Independent") : f.brand;
              mut.mutate({ name: f.name, brand, city: f.city, state: f.state, zip_code: f.zip_code,
                base_rate: Number(f.base_rate), star_rating: hotel?.star_rating ?? 3.5,
                address: f.address || `${f.city}, ${f.state}`,
                description: hotel?.description || `${f.name} offers negotiated corporate rates in ${f.city}, ${f.state}.`,
                amenities: hotel?.amenities || ["Free Wi-Fi", "Breakfast", "Parking", "24/7 Front Desk"], images,
                room_types: hotel?.room_types || [{ name: "Standard King", beds: "1 King", base_rate: Number(f.base_rate) }] }); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Saving…" : (isEdit ? "Save Changes" : "Publish Listing")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddHotelDialog() {
  const [open, setOpen] = useState(false);
  return (
    <HotelDialog open={open} onOpenChange={setOpen}
      trigger={
        <button data-testid="add-hotel-btn" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
          <PlusCircle size={18} weight="bold" /> Add listing
        </button>
      } />
  );
}

const TEAM_ROLE_OPTS = [
  { v: "gm", label: "General Manager" },
  { v: "rm", label: "Regional Manager" },
  { v: "dos", label: "Director of Sales" },
  { v: "sales", label: "Sales Manager" },
  { v: "front_desk", label: "Front Desk" },
];

function PropertyPicker({ hotels, selected, onToggle }) {
  return (
    <div className="border border-[#E5E7EB] max-h-44 overflow-y-auto" data-testid="property-picker">
      {hotels.length === 0 && <div className="px-3 py-3 text-sm text-[#9CA3AF]">Add a property first.</div>}
      {hotels.map((h) => (
        <label key={h.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-[#F3F4F6] cursor-pointer hover:bg-[#F9FAFB]">
          <input type="checkbox" data-testid={`prop-opt-${h.id}`} checked={selected.includes(h.id)} onChange={() => onToggle(h.id)}
            className="accent-[#002FA7]" />
          <span className="text-[#111827]">{h.name}</span>
          <span className="text-[#9CA3AF] text-xs ml-auto">{h.city}, {h.state}</span>
        </label>
      ))}
    </div>
  );
}

function AddTeamMemberDialog({ hotels }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { name: "", email: "", password: "", role: "gm", title: "", property_ids: [] };
  const [f, setF] = useState(blank);
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (id) => setF((s) => ({ ...s, property_ids: s.property_ids.includes(id) ? s.property_ids.filter((x) => x !== id) : [...s.property_ids, id] }));
  const mut = useMutation({
    mutationFn: (body) => api.createTeamMember(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Team member created — they can log in now"); setOpen(false); setF(blank); },
    onError: (e) => toast.error(errMsg(e, "Could not create team member")),
  });
  const needsProps = f.role !== "sales";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-team-btn" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
          <UserPlus size={16} weight="bold" /> Add team member
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add Team Member</DialogTitle></DialogHeader>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Role</span>
          <select data-testid="team-role" value={f.role} onChange={F("role")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {TEAM_ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Name</span>
          <input data-testid="team-name" value={f.name} onChange={F("name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Title (optional)</span>
          <input data-testid="team-title" value={f.title} onChange={F("title")} placeholder="Defaults to the role name" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Login email</span>
          <input data-testid="team-email" type="email" value={f.email} onChange={F("email")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Temp password</span>
          <input data-testid="team-password" value={f.password} onChange={F("password")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        {needsProps && (
          <div className="text-sm"><span className="overline text-[#4B5563] block mb-1">Assign properties ({f.property_ids.length})</span>
            <PropertyPicker hotels={hotels} selected={f.property_ids} onToggle={toggle} /></div>
        )}
        <DialogFooter>
          <button data-testid="save-team-btn" disabled={mut.isPending}
            onClick={() => { if (!f.name.trim() || !f.email.trim() || f.password.length < 6) return toast.error("Name, email and a 6+ char password required"); if (needsProps && f.property_ids.length === 0) return toast.error("Assign at least one property"); mut.mutate(f); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Creating…" : "Create member"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPropertiesDialog({ member, hotels }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(member.property_ids || []);
  const toggle = (id) => setIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const mut = useMutation({
    mutationFn: () => api.updateMemberProperties(member.id, ids),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Properties updated"); setOpen(false); },
    onError: (e) => toast.error(errMsg(e, "Could not update")),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setIds(member.property_ids || []); }}>
      <DialogTrigger asChild>
        <button data-testid={`edit-props-${member.id}`} className="text-sm font-semibold text-[#002FA7] hover:underline">Edit properties</button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">{member.name}'s properties</DialogTitle></DialogHeader>
        <PropertyPicker hotels={hotels} selected={ids} onToggle={toggle} />
        <DialogFooter>
          <button data-testid={`save-props-${member.id}`} disabled={mut.isPending} onClick={() => mut.mutate()}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Saving…" : "Save properties"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TEAM_ROLE_OPTIONS = [
  ["gm", "General Manager"], ["rm", "Regional Manager"], ["dos", "Director of Sales"],
  ["front_desk", "Front Desk"], ["sales", "Sales Manager"],
];
function ChangeRole({ member }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (role) => api.updateTeamMember(member.id, { role, property_ids: member.property_ids || [] }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Position updated"); },
    onError: (e) => toast.error(errMsg(e, "Could not change position")),
  });
  return (
    <label className="block mt-3">
      <span className="overline text-[#4B5563] block mb-1">Position</span>
      <select data-testid={`role-select-${member.id}`} value={member.role} disabled={mut.isPending}
        onChange={(e) => mut.mutate(e.target.value)}
        className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
        {TEAM_ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}


function AddLeadDialog({ hotels }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { company_name: "", category: "Corporate/Crew", industry: "", city: "",
                  email: "", phone: "", address: "", website: "", hotel_id: "" };
  const [f, setF] = useState(blank);
  const mut = useMutation({
    mutationFn: (body) => api.createLead(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead added"); setOpen(false); setF(blank); },
    onError: (e) => toast.error(errMsg(e, "Could not add lead")),
  });
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-lead-btn" className="inline-flex items-center gap-2 bg-[#111827] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#002FA7] transition-colors active:scale-[0.98]">
          <PlusCircle size={16} weight="bold" /> Add lead manually
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a Lead</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">For a company that isn't in the search — e.g. a referral or repeat group.</p>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Company name</span>
          <input data-testid="lead-company" value={f.company_name} onChange={F("company_name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Business type</span>
            <select data-testid="lead-category" value={f.category} onChange={F("category")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {LEAD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Industry</span>
            <input data-testid="lead-industry" value={f.industry} onChange={F("industry")} placeholder="e.g. Energy" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">City</span>
            <input data-testid="lead-city" value={f.city} onChange={F("city")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Phone</span>
            <input data-testid="lead-phone" value={f.phone} onChange={F("phone")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        </div>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Email</span>
          <input data-testid="lead-email" value={f.email} onChange={F("email")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Address</span>
          <input data-testid="lead-address" value={f.address} onChange={F("address")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Associated property (optional)</span>
          <select data-testid="lead-hotel" value={f.hotel_id} onChange={F("hotel_id")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            <option value="">— None —</option>
            {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select></label>
        <DialogFooter>
          <button data-testid="save-lead-btn" disabled={mut.isPending}
            onClick={() => { if (!f.company_name.trim()) return toast.error("Company name is required"); mut.mutate(f); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Adding…" : "Add lead"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "listings", label: "My Listings" },
  { id: "leads", label: "Leads" },
  { id: "requests", label: "Rate Requests" },
  { id: "space", label: "Rooms & Space" },
  { id: "calendar", label: "Calendar" },
  { id: "diary", label: "Function Diary" },
  { id: "accounts", label: "Accounts" },
  { id: "bookings", label: "Bookings" },
  { id: "reports", label: "Sales Report" },
  { id: "sales", label: "Team" },
  { id: "rfps", label: "RFPs" },
  { id: "contracts", label: "Contracts" },
];

export default function HotelDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const qc = useQueryClient();

  const { data: a } = useQuery({ queryKey: ["hAnalytics"], queryFn: api.hotelAnalytics });
  const { data: hotels = [] } = useQuery({ queryKey: ["hotels", "owner", user.id], queryFn: () => api.hotels({ owner_id: user.id }) });
  const { data: rfps = [] } = useQuery({ queryKey: ["rfps", "owner"], queryFn: () => api.rfps() });
  const { data: contracts = [] } = useQuery({ queryKey: ["contracts", "owner"], queryFn: () => api.contracts() });
  const { data: bookings = [] } = useQuery({ queryKey: ["bookings", "owner"], queryFn: () => api.bookings() });
  const { data: leads = [] } = useQuery({ queryKey: ["leads", "owner"], queryFn: () => api.leads() });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: api.listTeam });
  const { data: upcoming } = useQuery({ queryKey: ["upcomingReminders"], queryFn: api.upcomingReminders });

  const [leadQ, setLeadQ] = useState("");
  const [leadCat, setLeadCat] = useState("all");
  const [leadCity, setLeadCity] = useState("all");
  const [leadAssignee, setLeadAssignee] = useState("all");
  const [editHotel, setEditHotel] = useState(null);
  const hotelFilter = params.get("hotel") || "";
  const hotelFilterName = hotels.find((h) => h.id === hotelFilter)?.name;
  const leadCities = Array.from(new Set(leads.map((l) => l.city).filter(Boolean))).sort();
  const assigneeOptions = Array.from(
    leads.reduce((m, l) => { if (l.assigned_to) m.set(l.assigned_to, l.assigned_to_name || "Assigned"); return m; }, new Map())
  ).map(([id, name]) => ({ id, name }));
  const filteredLeads = leads.filter((l) => {
    if (hotelFilter && l.hotel_id !== hotelFilter) return false;
    if (leadCat !== "all" && l.category !== leadCat) return false;
    if (leadCity !== "all" && l.city !== leadCity) return false;
    if (leadAssignee === "mine" && l.assigned_to !== user.id) return false;
    if (leadAssignee === "unassigned" && l.assigned_to) return false;
    if (leadAssignee !== "all" && leadAssignee !== "mine" && leadAssignee !== "unassigned" && l.assigned_to !== leadAssignee) return false;
    if (leadQ) {
      const t = `${l.company_name} ${l.industry} ${l.email} ${l.phone} ${l.city} ${l.address}`.toLowerCase();
      if (!t.includes(leadQ.toLowerCase())) return false;
    }
    return true;
  });

  const del = useMutation({ mutationFn: (id) => api.deleteHotel(id), onSuccess: () => { qc.invalidateQueries(); toast.success("Listing removed"); } });
  const intakeLink = useMutation({
    mutationFn: (hotelId) => api.createIntakeLink({ hotel_id: hotelId }),
    onSuccess: (res) => {
      const url = `${window.location.origin}/intake/${res.token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Property requirement link copied to clipboard");
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del2 = useMutation({ mutationFn: (id) => api.removeTeamMember(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Team member removed"); }, onError: (e) => toast.error(errMsg(e)) });

  const today = new Date().toISOString().slice(0, 10);
  const quietCutoff = new Date(Date.now() - 14 * 86400000).toISOString();
  const propStats = (h) => {
    const hl = leads.filter((l) => l.hotel_id === h.id);
    const openLeads = hl.filter((l) => !["won", "lost"].includes(l.status));
    const open = openLeads.length;
    const due = hl.reduce((n, l) => n + (l.reminders || []).filter((r) => !r.done && (r.date || "") <= today).length, 0);
    const overdue = hl.reduce((n, l) => n + (l.reminders || []).filter((r) => !r.done && (r.date || "") < today).length, 0);
    const stale = openLeads.filter((l) => {
      const times = [l.created_at, ...(l.activities || []).map((a) => a.at)].filter(Boolean).sort();
      const last = times[times.length - 1] || "";
      return last < quietCutoff;
    }).length;
    const rev = (a?.per_hotel || []).find((p) => p.hotel === h.name)?.revenue || 0;
    const reasons = [];
    if (overdue > 0) reasons.push(`${overdue} overdue reminder${overdue > 1 ? "s" : ""}`);
    if (stale > 0) reasons.push(`${stale} lead${stale > 1 ? "s" : ""} gone quiet`);
    return { open, due, overdue, stale, rev, alert: reasons.length > 0, reasons };
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="overline text-[#002FA7]">Management Company / Group Admin</span>
          <h1 className="font-display font-black text-4xl text-[#111827] mt-2">{user.company_name || user.name}</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/prospect" data-testid="prospect-cta" className="inline-flex items-center gap-2 border border-[#111827] px-5 py-3 font-semibold hover:bg-[#111827] hover:text-white transition-colors active:scale-[0.98]">
            <Target size={18} weight="bold" /> Find prospects
          </Link>
          <AddHotelDialog />
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E5E7EB] mt-8 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#4B5563] hover:text-[#111827]"}`}>
            {t.label}{t.id === "leads" && leads.length > 0 ? ` (${leads.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB]">
            <Stat icon={Target} label="Leads" value={a?.leads ?? 0} />
            <Stat icon={Trophy} label="Leads Won" value={a?.leads_won ?? 0} accent="#047857" />
            <Stat icon={Handshake} label="Contracts Signed" value={a?.contracts_signed ?? 0} accent="#047857" />
            <Stat icon={CurrencyDollar} label="Revenue" value={money(a?.revenue)} accent="#047857" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB] mt-6">
            <Stat icon={Buildings} label="Listings" value={a?.listings ?? 0} />
            <Stat icon={FileText} label="RFPs Received" value={a?.total_rfps ?? 0} />
            <Stat icon={ChatsCircle} label="Active Negotiations" value={a?.active_negotiations ?? 0} accent="#D97706" />
            <Stat icon={CalendarCheck} label="Bookings" value={a?.bookings ?? 0} />
          </div>
          <div className="border border-[#E5E7EB] p-6 mt-6">
            <span className="overline text-[#4B5563]">Revenue by property</span>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a?.per_hotel || []} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="hotel" tick={{ fontSize: 11, fill: "#4B5563" }} interval={0} tickFormatter={(v) => v.length > 14 ? v.slice(0, 14) + "…" : v} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 0, border: "1px solid #111827", fontFamily: "IBM Plex Mono" }} />
                  <Bar dataKey="revenue" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-[#111827] p-6 mt-6" data-testid="upcoming-reminders">
            <div className="flex items-center gap-2">
              <BellRinging size={18} weight="bold" color="#B45309" />
              <span className="font-semibold text-[#111827]">Upcoming reminders & repeat business</span>
            </div>
            <div className="mt-4 space-y-2">
              {(!upcoming || upcoming.count === 0) && <p className="text-sm text-[#9CA3AF]">No reminders set. Open a lead to schedule a return date.</p>}
              {upcoming?.reminders?.slice(0, 8).map((r) => (
                <Link key={r.reminder_id} to={`/leads/${r.lead_id}`} data-testid={`reminder-item-${r.reminder_id}`}
                  className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#002FA7] transition-colors">
                  <span className="text-[#111827]"><b>{r.company_name}</b> — {r.note}</span>
                  <span className="font-mono text-[#B45309] font-semibold">{dateFmt(r.date)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "listings" && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
          {hotels.length === 0 && <Empty text="No listings yet. Add your first hotel." />}
          {hotels.map((h) => {
            const st = propStats(h);
            return (
            <div key={h.id} className={`rb-card flex flex-col ${st.alert ? "ring-2 ring-[#DC2626]" : ""}`} data-testid={`listing-${h.id}`}>
              <div className="relative h-40">
                <img src={h.images?.[0] || DEFAULT_HOTEL_IMAGE} alt={h.name} className="w-full h-full object-cover" />
                {h.premium && <span className="absolute top-2 left-2 bg-[#002FA7] text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Premium</span>}
                {st.alert && (
                  <span data-testid={`health-alert-${h.id}`} title={st.reasons.join(" · ")}
                    className="absolute top-2 right-2 bg-[#DC2626] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Warning size={12} weight="fill" /> Needs attention
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="overline text-[#4B5563]">{h.brand}</span>
                  <span className="flex items-center gap-1 text-xs font-semibold"><Star size={12} weight="fill" color="#D97706" /> {h.star_rating}</span>
                </div>
                <div className="font-display font-bold text-[#111827] mt-1">{h.name}</div>
                <div className="flex items-center gap-1 text-xs text-[#4B5563] mt-1"><MapPin size={12} weight="fill" /> {h.city}, {h.state}</div>
                <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`property-health-${h.id}`}>
                  <div className="bg-[#F3F4F6] px-2 py-1.5 text-center">
                    <div className="font-display font-bold text-[#002FA7] leading-none" data-testid={`ph-open-${h.id}`}>{st.open}</div>
                    <div className="text-[10px] text-[#4B5563] mt-0.5 uppercase tracking-wider">Open leads</div>
                  </div>
                  <div className={`px-2 py-1.5 text-center ${st.due > 0 ? "bg-[#FEF3C7]" : "bg-[#F3F4F6]"}`}>
                    <div className={`font-display font-bold leading-none ${st.due > 0 ? "text-[#B45309]" : "text-[#9CA3AF]"}`} data-testid={`ph-due-${h.id}`}>{st.due}</div>
                    <div className="text-[10px] text-[#4B5563] mt-0.5 uppercase tracking-wider">Due</div>
                  </div>
                  <div className="bg-[#F3F4F6] px-2 py-1.5 text-center">
                    <div className="font-display font-bold text-[#047857] leading-none text-sm" data-testid={`ph-rev-${h.id}`}>{money(st.rev)}</div>
                    <div className="text-[10px] text-[#4B5563] mt-0.5 uppercase tracking-wider">Revenue</div>
                  </div>
                </div>
                {st.alert && (
                  <div className="mt-2 text-[11px] text-[#DC2626] font-semibold flex items-center gap-1" data-testid={`health-reason-${h.id}`}>
                    <Warning size={12} weight="fill" /> {st.reasons.join(" · ")}
                  </div>
                )}
                <div className="flex items-center justify-between mt-auto pt-4">
                  <span className="font-mono font-semibold text-[#111827]">{money(h.base_rate)}/nt</span>
                  <div className="flex gap-3 items-center">
                    <button data-testid={`edit-listing-${h.id}`} onClick={() => setEditHotel(h)} className="text-sm font-semibold text-[#111827] hover:text-[#002FA7] transition-colors">Edit</button>
                    <button data-testid={`view-leads-${h.id}`} onClick={() => setParams({ tab: "leads", hotel: h.id })} className="text-sm font-semibold text-[#002FA7] hover:underline">View leads</button>
                    <Link to={`/prospect?hotel=${h.id}`} className="text-sm font-semibold text-[#4B5563] hover:text-[#002FA7]">Prospect</Link>
                    <button data-testid={`req-link-${h.id}`} onClick={() => intakeLink.mutate(h.id)} title="Copy a public link for this property so companies can submit their stay requirements"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#4B5563] hover:text-[#002FA7] transition-colors"><LinkSimple size={14} weight="bold" /> Req. link</button>
                    <button data-testid={`delete-listing-${h.id}`} onClick={() => del.mutate(h.id)} className="text-[#DC2626] hover:scale-110 transition-transform"><Trash size={16} weight="bold" /></button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
      {editHotel && (
        <HotelDialog hotel={editHotel} open={!!editHotel}
          onOpenChange={(o) => { if (!o) setEditHotel(null); }} />
      )}

      {tab === "leads" && (
        <div className="mt-8">
          {hotelFilter && (
            <div className="flex items-center justify-between bg-[#EEF2FF] border border-[#C7D2FE] px-4 py-3 mb-4" data-testid="lead-hotel-banner">
              <span className="text-sm text-[#1E3A8A]">Showing leads for <b>{hotelFilterName || "this property"}</b></span>
              <button data-testid="clear-hotel-filter" onClick={() => setParams({ tab: "leads" })} className="text-sm font-semibold text-[#002FA7] hover:underline">Show all leads</button>
            </div>
          )}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <span className="text-sm text-[#4B5563]">{filteredLeads.length} of {leads.length} leads</span>
            <AddLeadDialog hotels={hotels} />
          </div>
          <div className="grid md:grid-cols-12 gap-3 mb-5 bg-white border border-[#E5E7EB] p-3">
            <div className="md:col-span-5 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input data-testid="lead-search" value={leadQ} onChange={(e) => setLeadQ(e.target.value)}
                placeholder="Search leads by name, industry, email, phone, city…"
                className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
            <select data-testid="lead-filter-assignee" value={leadAssignee} onChange={(e) => setLeadAssignee(e.target.value)}
              className="md:col-span-3 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">Everyone's leads</option>
              <option value="mine">My leads</option>
              <option value="unassigned">Unassigned</option>
              {assigneeOptions.filter((o) => o.id !== user.id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <select data-testid="lead-filter-category" value={leadCat} onChange={(e) => setLeadCat(e.target.value)}
              className="md:col-span-2 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">All types</option>
              {LEAD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="lead-filter-city" value={leadCity} onChange={(e) => setLeadCity(e.target.value)}
              className="md:col-span-2 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">All locations</option>
              {leadCities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-3" data-testid="owner-leads-list">
            {leads.length === 0 && <Empty text="No leads yet. Use 'Find prospects' or 'Add lead manually'." />}
            {leads.length > 0 && filteredLeads.length === 0 && <Empty text="No leads match your filters." />}
            {filteredLeads.map((l) => (
              <Link key={l.id} to={`/leads/${l.id}`} data-testid={`owner-lead-${l.id}`} className="rb-card rb-card-hover flex items-center justify-between p-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-lg text-[#111827]">{l.company_name}</span>
                    {l.category && <span className="inline-flex items-center gap-1 bg-[#EDE9FE] text-[#6D28D9] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"><Tag size={10} weight="fill" /> {l.category}</span>}
                    {(l.reminders || []).some((r) => !r.done) && <span className="inline-flex items-center gap-1 text-[#B45309]" title="Has reminder"><BellRinging size={14} weight="fill" /></span>}
                  </div>
                  <div className="text-sm text-[#4B5563]">{l.industry || "Prospect"} · {l.city || "—"} · {l.phone || l.email || "no contact"}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#4B5563] hidden sm:block">{l.assigned_to_name ? `→ ${l.assigned_to_name}` : "Unassigned"}</span>
                  <StatusBadge status={l.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === "requests" && <GmRateRequests />}
      {tab === "space" && <SpaceTracker />}
      {tab === "calendar" && <EventsCalendar />}
      {tab === "diary" && <FunctionDiary />}
      {tab === "accounts" && <AccountsPanel />}
      {tab === "reports" && <SalesReport />}

      {tab === "sales" && (
        <div className="mt-8 space-y-8" data-testid="team-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><Users size={20} weight="duotone" color="#002FA7" /><span className="font-semibold text-[#111827]">Your team</span></div>
              <p className="text-sm text-[#4B5563] mt-1">Add General Managers, Regional Managers, Directors of Sales and Sales reps — and assign properties to each.</p>
            </div>
            <AddTeamMemberDialog hotels={hotels} />
          </div>
          {[
            { role: "rm", label: "Regional Managers", color: "#7C3AED" },
            { role: "gm", label: "General Managers", color: "#002FA7" },
            { role: "dos", label: "Directors of Sales", color: "#B45309" },
            { role: "front_desk", label: "Front Desk", color: "#0891B2" },
            { role: "sales", label: "Sales Team", color: "#111827" },
          ].map(({ role, label, color }) => {
            const members = team.filter((m) => m.role === role);
            return (
              <div key={role} data-testid={`team-group-${role}`}>
                <div className="overline text-[#4B5563] mb-3">{label} ({members.length})</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members.length === 0 && <Empty text={`No ${label.toLowerCase()} yet.`} />}
                  {members.map((m) => {
                    const assigned = leads.filter((l) => l.assigned_to === m.id);
                    const propNames = (m.property_ids || []).map((id) => hotels.find((h) => h.id === id)?.name).filter(Boolean);
                    return (
                      <div key={m.id} className="rb-card p-5" data-testid={`team-user-${m.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="w-10 h-10 text-white flex items-center justify-center font-display font-bold" style={{ background: color }}>{m.name[0]}</div>
                          <button data-testid={`remove-team-${m.id}`} onClick={() => del2.mutate(m.id)} className="text-[#DC2626] hover:scale-110 transition-transform"><Trash size={15} weight="bold" /></button>
                        </div>
                        <div className="font-display font-bold text-lg text-[#111827] mt-3">{m.name}</div>
                        <div className="text-sm text-[#4B5563]">{m.title}</div>
                        <div className="text-xs text-[#9CA3AF] mt-1">{m.email}</div>
                        <ChangeRole member={m} />
                        {role === "sales" ? (
                          <div className="mt-3 bg-[#E0E7FF] text-[#002FA7] px-3 py-1.5 text-sm font-semibold inline-block">{assigned.length} leads assigned</div>
                        ) : (
                          <div className="mt-3">
                            <div className="flex flex-wrap gap-1 mb-2">
                              {propNames.length === 0 && <span className="text-xs text-[#DC2626]">No properties assigned</span>}
                              {propNames.slice(0, 4).map((n) => <span key={n} className="text-[10px] bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5">{n}</span>)}
                              {propNames.length > 4 && <span className="text-[10px] text-[#9CA3AF]">+{propNames.length - 4}</span>}
                            </div>
                            <EditPropertiesDialog member={m} hotels={hotels} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "rfps" && (
        <div className="mt-8 space-y-3" data-testid="owner-rfp-list">
          {rfps.length === 0 && <Empty text="No RFPs received yet." />}
          {rfps.map((r) => (
            <Link key={r.id} to={`/negotiation/${r.id}`} data-testid={`owner-rfp-${r.id}`} className="rb-card rb-card-hover flex items-center justify-between p-5">
              <div>
                <div className="font-display font-bold text-lg text-[#111827]">{r.company_name}</div>
                <div className="text-sm text-[#4B5563]">{r.hotel_name} · {r.num_rooms} rooms · {dateFmt(r.check_in)}–{dateFmt(r.check_out)}</div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right hidden sm:block">
                  <div className="font-mono font-semibold text-[#111827]">Target {money(r.nightly_budget)}/nt</div>
                  <div className="text-xs text-[#4B5563]">{r.room_nights} room-nights</div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "contracts" && (
        <div className="mt-8 space-y-3" data-testid="owner-contract-list">
          {contracts.length === 0 && <Empty text="No contracts issued yet." />}
          {contracts.map((c) => (
            <div key={c.id} className="rb-card flex items-center justify-between p-5">
              <div>
                <div className="font-display font-bold text-lg text-[#111827]">{c.company_name}</div>
                <div className="text-sm text-[#4B5563]">{c.hotel_name} · {money(c.negotiated_rate)}/nt · {c.room_nights} room-nights</div>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={c.status} />
                <Link to={`/negotiation/${c.rfp_id}`} className="text-sm font-semibold text-[#002FA7] hover:underline">View →</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-8">
          <BookingsBoard />
          {bookings.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="owner-booking-list">
              <span className="overline text-[#4B5563] block">Marketplace confirmations</span>
              {bookings.map((b) => (
                <div key={b.id} className="rb-card flex items-center justify-between p-5">
                  <div>
                    <div className="font-display font-bold text-lg text-[#111827]">{b.company_name}</div>
                    <div className="text-sm text-[#4B5563]">{b.hotel_name} · {b.guest_name} · {b.rooms} rooms · {dateFmt(b.check_in)}–{dateFmt(b.check_out)}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-mono font-semibold text-[#111827]">{money(b.total)}</div>
                    <StatusBadge status={b.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="border border-dashed border-[#E5E7EB] py-16 text-center col-span-full">
      <Buildings size={32} weight="duotone" color="#9CA3AF" className="mx-auto" />
      <p className="text-[#4B5563] mt-3">{text}</p>
    </div>
  );
}
